import { DateTime } from "luxon";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import * as t from "io-ts";
import api, { createDecoder } from "../../api";
import { createErrorReportingAsyncThunk } from "../../helpers";
import { getTrailersAndRoutesResponseT, nullable } from "dora-contracts";
import {
  loadRouteWithStops,
  setSelectedTrailerId,
} from "../../data/route-with-stops";
import { drivingUrl } from "../../../routes";
import { logout } from "../../auth";

interface Trailer {
  id: string;
  number: string;
  alias: string | null;
}

interface Stop {
  city: string | null;
}

export interface Route {
  id: string;
  createdAt?: string | null;
  routeName: string | null;
  trailerId: string | null;
  destinationDate: string | null;
  completed: boolean;
  firstStop: Stop;
  lastStop: Stop;
}

interface State {
  status: "UNINITIALIZED" | "READY" | "LOAD_ERROR";
  trailers: Trailer[];
  unpoweredTrailers: Trailer[];
  routes: Route[];
}

const initialState: State = {
  status: "UNINITIALIZED",
  trailers: [],
  unpoweredTrailers: [],
  routes: [],
};

const prefix = "app/route-login";

export const init = createAsyncThunk(
  `${prefix}/init`,
  async (_: void, thunkApi) => {
    return await api(thunkApi).get({
      url: "/drivers-api/trailers",
      decoder: createDecoder(getTrailersAndRoutesResponseT),
      noChangeConnectionStateOnError: true,
    });
  }
);

const loginRouteResponseT = t.strict({ routeId: t.string });

export const loginRoute = createAsyncThunk(
  `${prefix}/login`,
  async (
    input: {
      routeId: string | null;
      trailerId: string;
      unpoweredTrailerId: string | null;
    },
    thunkApi
  ) => {
    const result = await api(thunkApi).post({
      url: "/drivers-api/sessions",
      body: { ...input, time: DateTime.now().toISO() },
      decoder: createDecoder(loginRouteResponseT),
    });
    return {
      ...result,
      trailerId: input.trailerId,
    };
  }
);

export const loginRouteWithLastUsedTrailerForRoute = createErrorReportingAsyncThunk(
  `${prefix}/loginRouteWithLastUsedTrailerForRoute`,
  async (input: { routeId: string }, thunkApi) => {
    const lastUsedTrailerResponse = await api(thunkApi).get({
      url: `/drivers-api/routes/${input.routeId}/last-used-trailer`,
      decoder: createDecoder(
        t.strict({
          trailerId: nullable(t.string),
        })
      ),
    });
    const trailerId = lastUsedTrailerResponse.trailerId;
    if (!trailerId) {
      throw new Error("Trailer id is null for last used trailer for route");
    }

    const { dispatch } = thunkApi;
    const { routeId } = await dispatch(
      loginRoute({ routeId: input.routeId, trailerId, unpoweredTrailerId: null })
    ).unwrap();
    await dispatch(loadRouteWithStops(routeId)).unwrap();
    dispatch(setSelectedTrailerId(lastUsedTrailerResponse.trailerId));
    return drivingUrl({ routeId, truckId: trailerId, trailerId: null });
  }
);

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(init.fulfilled, (state, action) => {
        state.status = "READY";
        state.trailers = action.payload.trailers;
        state.unpoweredTrailers = action.payload.unpoweredTrailers;
        state.routes = action.payload.routes;
      })
      .addCase(init.rejected, (state) => {
        state.status = "LOAD_ERROR";
      })
      .addCase(logout.fulfilled, (state) => {
        state.status = "UNINITIALIZED";
        state.trailers = [];
        state.unpoweredTrailers = [];
        state.routes = [];
      });
  },
});

export default slice.reducer;
